/**
 * Simple encryption/decryption utilities for URL parameters
 * Uses base64 encoding with a simple transformation for basic obfuscation
 */

const ENCRYPTION_KEY = 'dpn2024'; // Simple key for transformation

/**
 * Encrypt an ID for URL usage
 */
export const encryptId = (id: number): string => {
  try {
    // Convert ID to string and add some padding
    const idString = id.toString();
    const timestamp = Date.now().toString().slice(-4); // Last 4 digits of timestamp
    const combined = `${idString}-${timestamp}-${ENCRYPTION_KEY}`;
    
    // Base64 encode and make URL safe
    const encoded = btoa(combined)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    
    return encoded;
  } catch (error) {
    console.error('Error encrypting ID:', error);
    return id.toString();
  }
};

/**
 * Decrypt an ID from URL
 */
export const decryptId = (encryptedId: string): number | null => {
  try {
    // Restore base64 format
    let base64 = encryptedId
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    // Add padding if needed
    while (base64.length % 4) {
      base64 += '=';
    }
    
    // Decode and extract ID
    const decoded = atob(base64);
    const parts = decoded.split('-');
    
    if (parts.length >= 3 && parts[2] === ENCRYPTION_KEY) {
      const id = parseInt(parts[0], 10);
      return isNaN(id) ? null : id;
    }
    
    return null;
  } catch (error) {
    console.error('Error decrypting ID:', error);
    return null;
  }
};

/**
 * Create a URL-friendly slug from text
 */
export const createSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Create a URL-friendly slug from event title
 */
export const createEventSlug = (title: string): string => {
  return createSlug(title);
};

/**
 * Create a URL-friendly slug from location
 */
export const createLocationSlug = (location: string): string => {
  return createSlug(location);
};

/**
 * Generate event detail URL with location
 */
export const generateEventDetailUrl = (id: number, title: string, location?: string): string => {
  const encryptedId = encryptId(id);
  const locationSlug = location ? createLocationSlug(location) : 'location-tba';
  const titleSlug = createEventSlug(title);
  return `/event/${encryptedId}/${locationSlug}/${titleSlug}`;
};

/**
 * Parse event detail URL to extract ID, location, and name
 */
export const parseEventDetailUrl = (path: string): { 
  id: number | null; 
  locationSlug: string | null; 
  titleSlug: string | null 
} => {
  // Match pattern: /event/{encryptedId}/{locationSlug}/{titleSlug}
  const match = path.match(/^\/event\/([^\/]+)\/([^\/]+)\/(.+)$/);
  
  if (match) {
    const [, encryptedId, locationSlug, titleSlug] = match;
    const id = decryptId(encryptedId);
    return { id, locationSlug, titleSlug };
  }
  
  // Fallback: try old format /event/{encryptedId}/{slug}
  const oldMatch = path.match(/^\/event\/([^\/]+)\/(.+)$/);
  if (oldMatch) {
    const [, encryptedId, slug] = oldMatch;
    const id = decryptId(encryptedId);
    return { id, locationSlug: null, titleSlug: slug };
  }
  
  return { id: null, locationSlug: null, titleSlug: null };
};
